import React, { useEffect, useState } from 'react';
import _ from 'lodash';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import classNames from 'classnames';

import { RootState } from 'src/configs/store';
import useIsMobile from 'src/hooks/useIsMobile';
import Icon from 'src/components/Icon/Icon';

interface CardCodeProps {
  productType: number
}

function CardCodes({ productType }: CardCodeProps) {
  const { t } = useTranslation();
  const [codes, setCodes] = useState(null);
  const [simpleInfo, setSimpleInfo] = useState(false);
  const [orderBy, setOrderBy] = useState('couponCardCode');
  const dispatch = useDispatch();
  const isMobile = useIsMobile();

  const { selectedTimezone } = useSelector((state: RootState) => state.odds);

  useEffect(() => {
    if (productType === 4) {
      setSimpleInfo(true);
    }

    const getCodes = async () => {
      const eventCodes = await dispatch.events.getEventCodes(productType);

      if (eventCodes){
        setCodes(eventCodes);
      }

    };
    getCodes();
  }, [productType, dispatch.events]);

  const sort = (name) => {
    setOrderBy(name === orderBy ? '' : name);
    setCodes(_.orderBy(codes, name, name === orderBy ? 'asc' : 'desc'));
  };

  const renderDesktop = () => (
    <>
      <thead className="type-codes__header">
        <tr>
          <th className="type-codes__head type-codes__head--event">
            { t('typeCodes.tableCardCode') }
            <Icon
              onClick={ () => sort('couponCardCode') }
              className={
                classNames({
                  'type-codes__arrow': true,
                  'type-codes__arrow--sorted': orderBy === 'couponCardCode'
                })
              }
              icon="arrow"
            />
          </th>
          <th className="type-codes__head type-codes__head--event">
            { t('typeCodes.tableCouponCode') }
            <Icon
              onClick={ () => sort('couponCode') }
              className={
                classNames({
                  'type-codes__arrow': true,
                  'type-codes__arrow--sorted': orderBy === 'couponCode'
                })
              }
              icon="arrow"
            />
          </th>
          {
            !simpleInfo && <th className="type-codes__head">
              { t('typeCodes.tournamentName') }
              <Icon
                onClick={ () => sort('name') }
                className={
                  classNames({
                    'type-codes__arrow': true,
                    'type-codes__arrow--sorted': orderBy === 'name'
                  })
                }
                icon="arrow"
              />
            </th>
          }
          <th className="type-codes__head">{ t('typeCodes.eventName') }
            {
              simpleInfo && <Icon
                onClick={ () => sort('name') }
                className={
                  classNames({
                    'type-codes__arrow': true,
                    'type-codes__arrow--sorted': orderBy === 'name'
                  })
                }
                icon="arrow"
              />
            }
          </th>
          <th className="type-codes__head">
            { t('typeCodes.scheduled') }
            <Icon
              onClick={ () => sort('nextEventScheduled') }
              className={
                classNames({
                  'type-codes__arrow': true,
                  'type-codes__arrow--sorted': orderBy === 'nextEventScheduled'
                })
              }
              icon="arrow"
            />
          </th>
        </tr>
      </thead>
      <tbody>
        {
          codes &&
          _.map(codes, (event, index) => {
            const date = event?.nextEventScheduled && format(utcToZonedTime(event.nextEventScheduled * 1000, selectedTimezone), 'dd/MM-HH:mm');

            return (
              <tr className="type-codes__rows" key={ index }>
                <td>{ event.couponCardCode }</td>
                <td>{ event.couponCode }</td>
                <td>{ event.name }</td>
                { !simpleInfo && <td>{ event.nextEventName }</td> }
                <td>{ date }</td>
              </tr>
            );
          })
        }
      </tbody>
    </>
  );

  const renderMobile = () => (
    <>
      <thead className="type-codes__header">
        <tr>
          <th className="type-codes__head type-codes__head--event">{ t('typeCodes.tableCouponCode') }</th>
          <th className="type-codes__head type-codes__head--event">{ t('typeCodes.tableCardCode') }</th>
          <th className="type-codes__head">
            <div className="type-codes__head--columm">
              <span>{ t('typeCodes.tournamentName') }</span>
              { !simpleInfo && <span>{ t('typeCodes.eventName') }</span> }
              <span>{ t('typeCodes.scheduled') }</span>
            </div>
          </th>
        </tr>
      </thead>
      <tbody>
        {
          codes &&
          _.map(codes, (event, index) => {
            const date = event?.nextEventScheduled && format(utcToZonedTime(event.nextEventScheduled * 1000, selectedTimezone), 'dd/MM-HH:mm');

            return (
              <tr className="type-codes__rows" key={ index }>
                <td>{ event.couponCardCode }</td>
                <td>{ event.couponCode }</td>
                <td>
                  <div className="type-codes__rows--mobile">
                    <span>{ event.name }</span>
                    { !simpleInfo && <span>{ event.nextEventName }</span> }
                    <span>{ date }</span>
                  </div>
                </td>
              </tr>
            );
          })
        }
      </tbody>
    </>
  );

  if (isMobile) {
    return renderMobile();
  }

  return renderDesktop();
}

export default CardCodes;
